import { useEffect, useState } from "react";
import SupportChat from "./SupportChat";
import SupportEnquiryForm from "./SupportEnquiryForm";
import { getUserTickets } from "../../api/apiServices";

const STATUS_CONFIG = {
  pending: {
    label: "Waiting for Agent",
    color: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-200",
    dot: "bg-amber-400",
    icon: "⏳",
  },
  assigned: {
    label: "Agent Assigned",
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    dot: "bg-emerald-500",
    icon: "💬",
  },
  closed: {
    label: "Closed",
    color: "text-slate-500",
    bg: "bg-slate-50",
    border: "border-slate-200",
    dot: "bg-slate-400",
    icon: "✅",
  },
  resolved: {
    label: "Resolved",
    color: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
    dot: "bg-blue-400",
    icon: "✔️",
  },
};

function ContactScreen({ user }) {
  const [view, setView] = useState("list");
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await getUserTickets();
      setTickets(Array.isArray(res) ? res : res?.data || []);
    } catch (err) {
      console.error("Failed to load tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [user.userId]);

  const handleOpenChat = (ticket) => {
    setSelectedTicket(ticket);
    setView("chat");
  };

  const handleEnquirySubmitted = (data) => {
    setSelectedTicket(data);
    setView("chat");
    fetchTickets();
  };

  // ── Chat view ──
  if (view === "chat" && selectedTicket) {
    return (
      <div className="flex flex-col h-[calc(100vh-180px)]">
        <button
          onClick={() => { setView("list"); fetchTickets(); }}
          className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 transition-colors w-fit rounded-xl mb-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to My Tickets
        </button>
        <SupportChat user={user} initialTicket={selectedTicket} />
      </div>
    );
  }

  // ── New ticket form view ──
  if (view === "new") {
    return (
      <div className="flex flex-col h-[calc(100vh-180px)]">
        <button
          onClick={() => setView("list")}
          className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 transition-colors w-fit rounded-xl mb-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to My Tickets
        </button>
        <div className="flex-1 bg-white rounded-2xl shadow">
          <div className="bg-emerald-600 text-white p-4 font-semibold">New Support Ticket</div>
          <SupportEnquiryForm user={user} onEnquirySubmitted={handleEnquirySubmitted} />
        </div>
      </div>
    );
  }

  // ── Ticket list view (default) ──
  const activeTickets = tickets.filter(t => t.status === "pending" || t.status === "assigned");
  const pastTickets = tickets.filter(t => t.status === "closed" || t.status === "resolved");

  return (
    <div className="flex flex-col h-[calc(100vh-180px)] bg-white rounded-2xl shadow overflow-hidden">
      {/* Header */}
      <div className="bg-emerald-600 text-white px-5 py-4 flex items-center justify-between flex-shrink-0">
        <div>
          <h2 className="font-semibold text-lg">Customer Support</h2>
          <p className="text-emerald-100 text-xs mt-0.5">
            {tickets.length === 0 ? "No tickets yet" : `${tickets.length} ticket${tickets.length !== 1 ? "s" : ""} total`}
          </p>
        </div>
        <button
          onClick={() => setView("new")}
          className="flex items-center gap-2 bg-white text-emerald-700 hover:bg-emerald-50 font-medium text-sm px-4 py-2 rounded-xl transition-colors shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          New Ticket
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto bg-slate-50">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-500 text-sm">Loading your tickets…</p>
          </div>
        ) : tickets.length === 0 ? (
          <EmptyState onNewTicket={() => setView("new")} />
        ) : (
          <div className="p-4 space-y-6">
            {/* Active tickets */}
            {activeTickets.length > 0 && (
              <section>
                <SectionLabel text="Active" />
                <div className="space-y-3">
                  {activeTickets.map(ticket => (
                    <TicketCard
                      key={ticket.ticketId}
                      ticket={ticket}
                      onOpen={handleOpenChat}
                      interactive
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Past tickets */}
            {pastTickets.length > 0 && (
              <section>
                <SectionLabel text="Past Tickets" />
                <div className="space-y-3">
                  {pastTickets.map(ticket => (
                    <TicketCard key={ticket.ticketId} ticket={ticket} onOpen={handleOpenChat} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────

function SectionLabel({ text }) {
  return (
    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">{text}</p>
  );
}

function TicketCard({ ticket, onOpen, interactive = false }) {
  const status = ticket.status || "pending";
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const canChat = status === "assigned";

  const date = ticket.createdAt
    ? new Date(ticket.createdAt).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })
    : "—";

  return (
    <div
      className={`bg-white rounded-2xl border p-4 shadow-sm transition-all ${cfg.border} ${
        interactive ? "hover:shadow-md" : "opacity-80"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Category + date */}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base">{cfg.icon}</span>
            <span className="font-semibold text-slate-800 text-sm truncate">{ticket.category}</span>
          </div>

          {/* Description preview */}
          {ticket.description && (
            <p className="text-slate-500 text-xs line-clamp-2 mb-2">{ticket.description}</p>
          )}

          {/* Meta row */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${status === "assigned" ? "animate-pulse" : ""}`} />
              {cfg.label}
            </span>
            <span className="text-xs text-slate-400">{date}</span>
            <span className="text-xs text-slate-400 font-mono">#{ticket.ticketId?.slice(-6)}</span>
          </div>
        </div>

        {/* Action button */}
        {canChat && (
          <button
            onClick={() => onOpen(ticket)}
            className="flex-shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-4 py-2 rounded-xl transition-colors"
          >
            Open Chat
          </button>
        )}

        {status === "pending" && (
          <span className="flex-shrink-0 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-3 py-2 rounded-xl font-medium">
            Pending…
          </span>
        )}
      </div>
    </div>
  );
}

function EmptyState({ onNewTicket }) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
      <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center text-3xl mb-4">
        🎧
      </div>
      <h3 className="text-lg font-semibold text-slate-800 mb-1">No tickets yet</h3>
      <p className="text-slate-500 text-sm mb-6 max-w-xs">
        Need help? Create a support ticket and an agent will be with you shortly.
      </p>
      <button
        onClick={onNewTicket}
        className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-6 py-3 rounded-2xl transition-colors"
      >
        Create Your First Ticket
      </button>
    </div>
  );
}

export default ContactScreen;
